import type { PortDirection, SignalCategory } from '@routr/shared'

/**
 * Project presets: finished rigs a workspace can drop in with one click, so a
 * new user sees a canvas, run sheet and power readout before drawing anything.
 *
 * This file is data only — no Prisma — so the projects page can list presets
 * and their counts without a round trip. `build.ts` turns a preset into rows.
 *
 * Every device is looked up in the catalogue by manufacturer + model, and
 * every cable by port name (prefix match, first free port with that signal).
 * A preset refuses to build when any device is missing; a cable whose port
 * has been renamed is reported as unpatched. `npm run check:presets` resolves
 * all of them against the local database so drift is caught before a deploy.
 */

export type PlacedSpec = {
  /** Referenced by patches. Unique within a preset. */
  key: string
  manufacturer: string
  model: string
  /** Instance label on the canvas: "Camera 1", "Host mic". */
  label: string
  x: number
  y: number
}

/** A cable as (fromKey, from-port prefix, toKey, to-port prefix, run label). */
export type PatchSpec = { from: string; fromPort: string; to: string; toPort: string; label?: string }

export type PortSpec = {
  name: string
  direction: PortDirection
  connector: string
  signal: SignalCategory
}

/**
 * Computers are not catalogue entries — every workstation is its own build —
 * so a preset that needs one describes it and the builder creates it as a
 * workspace-private device the first time, then reuses it.
 */
export type ComputerSpec = {
  manufacturer: string
  model: string
  name: string
  formFactorRU: number
  cpu?: string
  ram?: string
  gpu?: string
  storage?: string
  os?: string
  notes: string
  ports: PortSpec[]
  /** PCIe slots with the catalogue cards to install, in preference order; a missing card leaves the slot empty. */
  slots?: { label: string; cards: string[] }[]
}

export type Preset = {
  key: string
  name: string
  /** One line on the picker card. */
  tagline: string
  /** Stored on the project. */
  description: string
  /** The format the show runs at; drives cable-reach checks. */
  showFormat?: string
  devices: PlacedSpec[]
  patches: PatchSpec[]
  computers?: ComputerSpec[]
}

const CUSTOM = 'Custom build'

/** The laptop that records a podcast or drives a small stream. */
const PRODUCER_LAPTOP: ComputerSpec = {
  manufacturer: CUSTOM,
  model: 'Producer laptop',
  name: 'Laptop',
  formFactorRU: 0,
  notes: 'Generated with a project preset. Edit or delete freely.',
  ports: [
    { name: 'USB-C 1', direction: 'BIDIRECTIONAL', connector: 'USB-C', signal: 'DATA' },
    { name: 'USB-C 2', direction: 'BIDIRECTIONAL', connector: 'USB-C', signal: 'DATA' },
    { name: 'Ethernet', direction: 'BIDIRECTIONAL', connector: 'RJ45', signal: 'NETWORK' },
    { name: 'HDMI Out', direction: 'OUTPUT', connector: 'HDMI', signal: 'VIDEO' },
    { name: 'USB-C Power In', direction: 'INPUT', connector: 'USB-C (Power)', signal: 'POWER' },
  ],
}

/** The replay workstation in the live-stream rig: two DeckLinks in its slots. */
const REPLAY_PC: ComputerSpec = {
  manufacturer: CUSTOM,
  model: 'Replay PC',
  name: 'Workstation',
  formFactorRU: 4,
  cpu: 'AMD Ryzen 9 7950X',
  ram: '64 GB',
  gpu: 'NVIDIA RTX A4000',
  storage: '2 × 4 TB NVMe',
  os: 'Windows 11 Pro',
  notes: 'Generated with the sample project. Edit or delete freely.',
  ports: [
    { name: 'Ethernet', direction: 'INPUT', connector: 'RJ45', signal: 'NETWORK' },
    { name: 'AC In', direction: 'INPUT', connector: 'IEC C14', signal: 'POWER' },
    { name: 'DisplayPort Out', direction: 'OUTPUT', connector: 'DisplayPort', signal: 'VIDEO' },
  ],
  slots: [
    { label: 'Slot 1', cards: ['DeckLink 8K Pro G2', 'DeckLink Duo 2'] },
    { label: 'Slot 2', cards: ['DeckLink Mini Monitor 4K'] },
  ],
}

const BMD = 'Blackmagic Design'

export const PRESETS: Preset[] = [
  {
    key: 'podcast',
    name: 'Two-host podcast',
    tagline: 'Two mics into a RODECaster Duo, recorded on a laptop.',
    description:
      'Generated preset: host and guest microphones into a RODECaster Duo, with the multitrack recording on a laptop over USB-C. Edit or delete freely.',
    devices: [
      { key: 'host', manufacturer: 'RODE', model: 'PodMic', label: 'Host mic', x: 0, y: 0 },
      { key: 'guest', manufacturer: 'RODE', model: 'PodMic', label: 'Guest mic', x: 0, y: 300 },
      { key: 'desk', manufacturer: 'RODE', model: 'RODECaster Duo', label: 'Podcast desk', x: 460, y: 80 },
      { key: 'laptop', manufacturer: CUSTOM, model: 'Producer laptop', label: 'Recording laptop', x: 920, y: 80 },
    ],
    patches: [
      { from: 'host', fromPort: 'XLR Out', to: 'desk', toPort: 'Combo In 1', label: 'A-001' },
      { from: 'guest', fromPort: 'XLR Out', to: 'desk', toPort: 'Combo In 2', label: 'A-002' },
      { from: 'desk', fromPort: 'USB-C 1', to: 'laptop', toPort: 'USB-C 1' },
    ],
    computers: [PRODUCER_LAPTOP],
  },
  {
    key: 'video-podcast',
    name: 'Video podcast',
    tagline: 'Two Mevo cameras and two mics into a RODECaster Video.',
    description:
      'Generated preset: a camera and a microphone per seat into a RODECaster Video, with a laptop on USB-C for the stream and on Ethernet for control. Edit or delete freely.',
    showFormat: '1080p25',
    devices: [
      { key: 'cam1', manufacturer: 'Logitech', model: 'Mevo Core', label: 'Host camera', x: 0, y: 0 },
      { key: 'cam2', manufacturer: 'Logitech', model: 'Mevo Core', label: 'Guest camera', x: 0, y: 320 },
      { key: 'host', manufacturer: 'RODE', model: 'PodMic', label: 'Host mic', x: 0, y: 640 },
      { key: 'guest', manufacturer: 'RODE', model: 'PodMic', label: 'Guest mic', x: 0, y: 940 },
      { key: 'sw', manufacturer: 'RODE', model: 'RODECaster Video', label: 'Podcast switcher', x: 460, y: 360 },
      { key: 'laptop', manufacturer: CUSTOM, model: 'Producer laptop', label: 'Stream laptop', x: 920, y: 360 },
    ],
    patches: [
      { from: 'cam1', fromPort: 'HDMI Out', to: 'sw', toPort: 'HDMI In 1', label: 'V-001' },
      { from: 'cam2', fromPort: 'HDMI Out', to: 'sw', toPort: 'HDMI In 2', label: 'V-002' },
      { from: 'host', fromPort: 'XLR Out', to: 'sw', toPort: 'Combo In 1', label: 'A-001' },
      { from: 'guest', fromPort: 'XLR Out', to: 'sw', toPort: 'Combo In 2', label: 'A-002' },
      { from: 'sw', fromPort: 'USB-C 1', to: 'laptop', toPort: 'USB-C 1' },
      { from: 'laptop', fromPort: 'Ethernet', to: 'sw', toPort: 'Ethernet' },
    ],
    computers: [PRODUCER_LAPTOP],
  },
  {
    key: 'single-camera',
    name: 'Single-camera stream',
    tagline: 'One camera and a mic through an ATEM Mini Pro ISO to a laptop.',
    description:
      'Generated preset: a cinema camera over HDMI and a broadcast mic through a Vocaster into an ATEM Mini Pro ISO, which feeds the laptop as a webcam. Edit or delete freely.',
    showFormat: '1080p50',
    devices: [
      { key: 'cam', manufacturer: BMD, model: 'Blackmagic Pocket Cinema Camera 6K Pro', label: 'Camera', x: 0, y: 0 },
      { key: 'mic', manufacturer: 'Shure', model: 'SM7B', label: 'Presenter mic', x: 0, y: 420 },
      { key: 'iface', manufacturer: 'Focusrite', model: 'Vocaster One', label: 'Mic interface', x: 460, y: 420 },
      { key: 'sw', manufacturer: BMD, model: 'ATEM Mini Pro ISO', label: 'Switcher', x: 460, y: 0 },
      { key: 'laptop', manufacturer: CUSTOM, model: 'Producer laptop', label: 'Stream laptop', x: 920, y: 0 },
    ],
    patches: [
      { from: 'cam', fromPort: 'HDMI Out', to: 'sw', toPort: 'HDMI In 1', label: 'V-001' },
      { from: 'mic', fromPort: 'XLR Out', to: 'iface', toPort: 'Mic In', label: 'A-001' },
      { from: 'iface', fromPort: 'Camera Out', to: 'sw', toPort: 'Mic In 1', label: 'A-002' },
      { from: 'sw', fromPort: 'USB-C', to: 'laptop', toPort: 'USB-C 1' },
      { from: 'laptop', fromPort: 'Ethernet', to: 'sw', toPort: 'Ethernet' },
    ],
    computers: [PRODUCER_LAPTOP],
  },
  {
    key: 'live-stream',
    name: 'Four-camera live stream',
    tagline: 'Studio cameras into an ATEM with ISO record, multiview, replay and rack power.',
    description:
      'Generated example: four studio cameras into an ATEM, ISO record, multiview, stream feed, a replay PC with two DeckLinks, Dante audio, control network and rack power. Edit or delete freely.',
    showFormat: '1080p50',
    devices: [
      { key: 'cam1', manufacturer: BMD, model: 'Blackmagic Studio Camera 6K Pro', label: 'Camera 1', x: 0, y: 0 },
      { key: 'cam2', manufacturer: BMD, model: 'Blackmagic Studio Camera 6K Pro', label: 'Camera 2', x: 0, y: 320 },
      { key: 'cam3', manufacturer: BMD, model: 'Blackmagic Studio Camera 4K Pro G2', label: 'Camera 3', x: 0, y: 640 },
      { key: 'cam4', manufacturer: BMD, model: 'Blackmagic Studio Camera 4K Pro G2', label: 'Camera 4', x: 0, y: 960 },
      { key: 'sw', manufacturer: BMD, model: 'ATEM 1 M/E Constellation HD', label: 'Switcher', x: 460, y: 280 },
      { key: 'audio', manufacturer: 'Yamaha', model: 'QL5', label: 'FOH / Broadcast mix', x: 460, y: 900 },
      { key: 'rec', manufacturer: BMD, model: 'HyperDeck Studio HD Pro', label: 'ISO Recorder', x: 920, y: 0 },
      { key: 'mv', manufacturer: BMD, model: 'Blackmagic MultiView 4', label: 'Gallery multiview', x: 920, y: 380 },
      { key: 'enc', manufacturer: BMD, model: 'Mini Converter SDI to HDMI 6G', label: 'Stream encoder feed', x: 920, y: 700 },
      { key: 'net', manufacturer: 'Cisco', model: 'CBS350-24T-4X', label: 'Control switch', x: 920, y: 1000 },
      { key: 'pc', manufacturer: CUSTOM, model: 'Replay PC', label: 'Replay PC', x: 1380, y: 0 },
      { key: 'pdu', manufacturer: 'APC', model: 'AP7921B', label: 'Rack PDU', x: 1380, y: 700 },
    ],
    patches: [
      { from: 'cam1', fromPort: 'SDI Out', to: 'sw', toPort: 'SDI In 1', label: 'V-001' },
      { from: 'cam2', fromPort: 'SDI Out', to: 'sw', toPort: 'SDI In 2', label: 'V-002' },
      { from: 'cam3', fromPort: 'SDI Out', to: 'sw', toPort: 'SDI In 3', label: 'V-003' },
      { from: 'cam4', fromPort: 'SDI Out', to: 'sw', toPort: 'SDI In 4', label: 'V-004' },
      { from: 'sw', fromPort: 'SDI Out 1', to: 'rec', toPort: 'SDI In' },
      { from: 'sw', fromPort: 'SDI Out 2', to: 'mv', toPort: 'SDI In 1' },
      { from: 'sw', fromPort: 'SDI Out 3', to: 'enc', toPort: 'SDI In' },
      { from: 'rec', fromPort: 'SDI Out', to: 'mv', toPort: 'SDI In 2' },
      { from: 'audio', fromPort: 'Omni Out 1', to: 'sw', toPort: 'Analog Audio In 1', label: 'A-001' },
      { from: 'audio', fromPort: 'Omni Out 2', to: 'sw', toPort: 'Analog Audio In 2', label: 'A-002' },
      { from: 'net', fromPort: 'GbE 1', to: 'sw', toPort: 'Ethernet' },
      { from: 'net', fromPort: 'GbE 2', to: 'rec', toPort: 'Ethernet' },
      { from: 'net', fromPort: 'GbE 3', to: 'mv', toPort: 'Ethernet' },
      { from: 'pdu', fromPort: 'Outlet 1', to: 'sw', toPort: 'AC' },
      { from: 'pdu', fromPort: 'Outlet 2', to: 'rec', toPort: 'AC' },
      { from: 'pdu', fromPort: 'Outlet 3', to: 'mv', toPort: 'AC' },
      { from: 'pdu', fromPort: 'Outlet 4', to: 'net', toPort: 'AC' },
      { from: 'pdu', fromPort: 'Outlet 5', to: 'audio', toPort: 'AC' },
      { from: 'pdu', fromPort: 'Outlet 6', to: 'enc', toPort: 'DC In' },
      // The replay PC: a switcher feed into its 8K Pro, its outputs into the
      // multiview, plus network and power on the chassis.
      { from: 'sw', fromPort: 'SDI Out 4', to: 'pc', toPort: 'SDI 1', label: 'V-005' },
      { from: 'pc', fromPort: 'SDI 2', to: 'mv', toPort: 'SDI In 3' },
      { from: 'pc', fromPort: 'SDI Out', to: 'mv', toPort: 'SDI In 4' },
      { from: 'net', fromPort: 'GbE 4', to: 'pc', toPort: 'Ethernet' },
      { from: 'pdu', fromPort: 'Outlet 7', to: 'pc', toPort: 'AC' },
    ],
    computers: [REPLAY_PC],
  },
]

export const PRESET_KEYS = PRESETS.map((preset) => preset.key) as [string, ...string[]]

export type PresetKey = (typeof PRESETS)[number]['key']

export function findPreset(key: string): Preset | undefined {
  return PRESETS.find((preset) => preset.key === key)
}

/** What the picker prints under each name. */
export function presetSummary(preset: Preset): { devices: number; cables: number } {
  return { devices: preset.devices.length, cables: preset.patches.length }
}

/** The picker's view of a preset: everything except the specs. */
export type PresetCard = { key: string; name: string; tagline: string; devices: number; cables: number }

export const PRESET_CARDS: PresetCard[] = PRESETS.map((preset) => ({
  key: preset.key,
  name: preset.name,
  tagline: preset.tagline,
  ...presetSummary(preset),
}))
